import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  FileVideo,
  GripVertical,
  Highlighter,
  Loader,
  Pencil,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  useCancelSopGeneration,
  useCreateSopSection,
  useDeleteSop,
  useRegenerateSop,
  useReorderSopSections,
  useSaveSopMarkup,
  useSop,
  useUpdateSop,
  useUpdateSopSection,
} from '../hooks.ts';
import GenerationProgress from '../components/GenerationProgress.tsx';
import SopSectionBody from '../components/SopSectionBody.tsx';
import SopSectionEditor from '../components/SopSectionEditor.tsx';
import CitationPanel from '../components/CitationPanel.tsx';
import SopMarkupLayer from '../markup/SopMarkupLayer.tsx';
import { canEditSopMarkup, parseSopMarkup } from '../markup/types.ts';
import { formatDuration, formatWhen } from '../format.ts';
import { downloadSopDoc } from '../pdf/downloadSopDoc.ts';
import { runSopPdfDownload } from '../pdf/runSopPdfDownload.tsx';
import {
  appendSubsection,
  joinSectionBody,
  replaceSubsectionBody,
  splitSectionBody,
} from '../sectionBodyFormat.ts';
import {
  isSopEditable,
  LOCKED_SOP_MESSAGE,
  type Sop,
  type SopMarkupAnnotation,
  type SopSection,
  type SopWorkflowStatus,
} from '../../../request/sop-synthesizer/sopSynthesizer.api.ts';

const NAVY = '#1e40af';

function shortSopId(id: string): string {
  return id.replace(/-/g, '').slice(0, 8).toUpperCase();
}

function statusPillKey(status: string): SopWorkflowStatus {
  if (status === 'in_review') return 'in_review';
  if (status === 'changes_requested') return 'changes_requested';
  if (status === 'approved') return 'approved';
  if (status === 'published') return 'published';
  return 'draft';
}

function EditDetailsForm({ sop, onDone }: { sop: Sop; onDone: () => void }) {
  const update = useUpdateSop(sop.id);
  const [title, setTitle] = useState(sop.title);
  const [description, setDescription] = useState(sop.description ?? '');
  const invalid = !title.trim();
  return (
    <form
      className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (!invalid) update.mutate({ title: title.trim(), description: description.trim() }, { onSuccess: onDone });
      }}
    >
      <div>
        <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700">Title</label>
        <input id="edit-title" className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={title} maxLength={255} onChange={(e) => setTitle(e.target.value)} />
        {invalid && <p className="mt-1 text-xs text-red-600">Enter a title</p>}
      </div>
      <div>
        <label htmlFor="edit-desc" className="block text-sm font-medium text-gray-700">Description</label>
        <textarea id="edit-desc" className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" rows={3} value={description} maxLength={4000} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
        <button type="submit" disabled={invalid || update.isPending} className="rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50" style={{ backgroundColor: NAVY }}>
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

function StatusPills({
  status,
  disabled,
  pending,
  onChange,
}: {
  status: string;
  disabled?: boolean;
  pending?: boolean;
  onChange: (next: SopWorkflowStatus) => void;
}) {
  const active = statusPillKey(status);
  const items: { key: SopWorkflowStatus; label: string }[] = [
    { key: 'draft', label: 'Draft' },
    { key: 'in_review', label: 'In Review' },
    { key: 'changes_requested', label: 'Changes requested' },
    { key: 'approved', label: 'Approved' },
    { key: 'published', label: 'Published' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[11px] font-semibold tracking-wide text-gray-500">STATUS</span>
      <div className="inline-flex max-w-full flex-wrap rounded-full border border-gray-200 bg-gray-100 p-0.5" role="group" aria-label="SOP status">
        {items.map((item) => {
          const on = item.key === active;
          const activeClass =
            item.key === 'approved' || item.key === 'published'
              ? 'bg-emerald-500 text-white shadow-sm'
              : item.key === 'in_review'
                ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-300 shadow-sm'
                : item.key === 'changes_requested'
                  ? 'bg-red-100 text-red-800 ring-1 ring-red-300 shadow-sm'
                  : 'bg-white text-gray-900 shadow-sm';
          return (
            <button
              key={item.key}
              type="button"
              disabled={disabled || pending || on}
              aria-pressed={on}
              onClick={() => onChange(item.key)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                on ? activeClass : 'text-gray-500 hover:text-gray-800 disabled:hover:text-gray-500'
              } disabled:cursor-default`}
            >
              {on && (item.key === 'approved' || item.key === 'published') && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
              {on && item.key === 'in_review' && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
              {on && item.key === 'changes_requested' && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}
              {on && item.key === 'draft' && <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />}
              {!on && <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />}
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SectionCard({
  sopId,
  section,
  index,
  total,
  frames,
  knownMarkers,
  onCite,
  editable,
  saving,
  reordering,
  onMoveUp,
  onMoveDown,
  onBodySave,
  markupMode,
  markupAnnotations,
  markupEditable,
  onMarkupAdd,
  onMarkupRemove,
}: {
  sopId: string;
  section: SopSection;
  index: number;
  total: number;
  frames: Record<string, string>;
  knownMarkers: Set<string>;
  onCite: (marker: string) => void;
  editable: boolean;
  saving: boolean;
  reordering: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onBodySave: (sectionId: string, body: string, onDone: () => void, heading?: string) => void;
  markupMode: boolean;
  markupAnnotations: SopMarkupAnnotation[];
  markupEditable: boolean;
  onMarkupAdd: (item: SopMarkupAnnotation) => void;
  onMarkupRemove: (id: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addingSub, setAddingSub] = useState(false);
  const [subTitle, setSubTitle] = useState('');
  const [editingSubIndex, setEditingSubIndex] = useState<number | null>(null);
  const [draftHeading, setDraftHeading] = useState(section.heading);
  const [headingError, setHeadingError] = useState('');
  const title = `${index + 1}. ${section.heading}`.toUpperCase();
  const { preamble, subsections } = splitSectionBody(section.body);

  return (
    <section id={`sec-${section.id}`} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 px-3 py-2.5 text-white sm:px-4" style={{ backgroundColor: NAVY }}>
        <GripVertical className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
        {editing ? (
          <div className="min-w-0 flex-1">
            <input
              className="w-full rounded border border-white/30 bg-white/10 px-2 py-1 text-sm font-semibold tracking-wide text-white placeholder:text-white/50 outline-none focus:border-white/60"
              value={draftHeading}
              maxLength={255}
              disabled={saving}
              aria-label="Section heading"
              aria-invalid={!!headingError}
              onChange={(e) => {
                setDraftHeading(e.target.value);
                if (headingError) setHeadingError('');
              }}
            />
            {headingError ? <p className="mt-1 text-xs text-red-200">{headingError}</p> : null}
          </div>
        ) : (
          <h2 className="min-w-0 flex-1 truncate text-sm font-semibold tracking-wide">{title}</h2>
        )}
        <div className="flex shrink-0 items-center gap-0.5">
          {editable && !editing && (
            <>
              <button
                type="button"
                disabled={reordering || index === 0}
                onClick={onMoveUp}
                className="rounded p-1.5 text-white/90 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Move section up"
                title="Move up"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={reordering || index >= total - 1}
                onClick={onMoveDown}
                className="rounded p-1.5 text-white/90 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Move section down"
                title="Move down"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
            </>
          )}
          {!editing && (
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="rounded p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand section' : 'Collapse section'}
            >
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          )}
          {editable && !editing && editingSubIndex === null && (
            <button
              type="button"
              onClick={() => {
                setDraftHeading(section.heading);
                setHeadingError('');
                setEditing(true);
                setEditingSubIndex(null);
                setAddingSub(false);
                setCollapsed(false);
              }}
              className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs font-medium text-white/90 hover:bg-white/10"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          )}
          {/* Section delete — enable when implemented
          <button
            type="button"
            disabled
            title="Section delete will be available later"
            className="rounded p-1.5 text-white/40"
            aria-label="Remove section (coming later)"
          >
            <X className="h-4 w-4" />
          </button>
          */}
        </div>
      </div>

      {!collapsed && (
        <div className="border-t border-gray-100 px-4 py-4 sm:px-5">
          <SopMarkupLayer
            sectionId={section.id}
            sectionHeading={section.heading}
            annotations={markupAnnotations}
            markupMode={markupMode && !editing && editingSubIndex === null}
            canEdit={markupEditable}
            onAdd={onMarkupAdd}
            onRemove={onMarkupRemove}
          >
          {editing ? (
            <SopSectionEditor
              key={`sec-edit-${section.id}`}
              sopId={sopId}
              body={preamble}
              frames={frames}
              saving={saving}
              onSave={(markdown) => {
                const h = draftHeading.trim();
                if (!h) {
                  setHeadingError('heading is required');
                  return;
                }
                if (h.length > 255) {
                  setHeadingError('heading is too long');
                  return;
                }
                // Outer edit is preamble only — keep existing ## sub-sections as-is.
                onBodySave(section.id, joinSectionBody(markdown, subsections), () => setEditing(false), h);
              }}
              onCancel={() => {
                if (!saving) {
                  setEditing(false);
                  setHeadingError('');
                  setDraftHeading(section.heading);
                }
              }}
            />
          ) : preamble.trim() ? (
            <SopSectionBody
              body={preamble}
              frames={frames}
              knownMarkers={knownMarkers}
              onCite={onCite}
              sectionId={section.id}
              sectionHeading={section.heading}
              markupAnnotations={markupAnnotations}
              markupMode={markupMode && !editing && editingSubIndex === null}
              markupEditable={markupEditable}
              onMarkupAdd={onMarkupAdd}
              onMarkupRemove={onMarkupRemove}
            />
          ) : null}

          {subsections.length > 0 && (
            <div className={`space-y-3 ${editing || preamble.trim() ? 'mt-4' : ''}`}>
              {subsections.map((sub, si) => (
                <div
                  key={`${section.id}-sub-${si}-${sub.title}`}
                  className="overflow-hidden rounded-md border border-gray-200 border-l-4 border-l-blue-600 bg-gray-50"
                >
                  <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-100 px-3 py-2">
                    <h3 className="min-w-0 flex-1 truncate text-xs font-semibold tracking-wide text-gray-800">
                      {sub.title.toUpperCase()}
                    </h3>
                    {editable && !editing && editingSubIndex !== si && (
                      <button
                        type="button"
                        disabled={saving || editingSubIndex !== null}
                        onClick={() => {
                          setEditingSubIndex(si);
                          setAddingSub(false);
                        }}
                        className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                    )}
                    {/* Sub-section delete — enable when implemented
                    <button
                      type="button"
                      disabled
                      title="Sub-section delete will be available later"
                      className="rounded p-1 text-gray-400"
                      aria-label="Remove sub-section (coming later)"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    */}
                  </div>
                  <div className="bg-white px-3 py-3">
                    {editingSubIndex === si ? (
                      <SopSectionEditor
                        key={`sub-edit-${si}`}
                        sopId={sopId}
                        body={sub.body}
                        frames={frames}
                        saving={saving}
                        onSave={(markdown) => {
                          const next = replaceSubsectionBody(section.body, si, markdown);
                          onBodySave(section.id, next, () => setEditingSubIndex(null));
                        }}
                        onCancel={() => {
                          if (!saving) setEditingSubIndex(null);
                        }}
                      />
                    ) : sub.body.trim() ? (
                      <SopSectionBody
                        body={sub.body}
                        frames={frames}
                        knownMarkers={knownMarkers}
                        onCite={onCite}
                        sectionId={section.id}
                        sectionHeading={section.heading}
                        markupAnnotations={markupAnnotations}
                        markupMode={markupMode && !editing && editingSubIndex === null}
                        markupEditable={markupEditable}
                        onMarkupAdd={onMarkupAdd}
                        onMarkupRemove={onMarkupRemove}
                      />
                    ) : (
                      <p className="text-sm text-gray-400">No content yet.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          </SopMarkupLayer>

          {editable && !editing && (
            <div className="mt-4 border-t border-gray-100 pt-3">
              {addingSub ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    className="min-w-[12rem] flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                    placeholder="Sub-section title..."
                    value={subTitle}
                    onChange={(e) => setSubTitle(e.target.value)}
                    disabled={saving}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const t = subTitle.trim();
                        if (!t || saving) return;
                        onBodySave(section.id, appendSubsection(section.body, t), () => {
                          setAddingSub(false);
                          setSubTitle('');
                        });
                      }
                    }}
                  />
                  <button
                    type="button"
                    disabled={saving || !subTitle.trim()}
                    onClick={() => {
                      const t = subTitle.trim();
                      if (!t || saving) return;
                      onBodySave(section.id, appendSubsection(section.body, t), () => {
                        setAddingSub(false);
                        setSubTitle('');
                      });
                    }}
                    className="rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                    style={{ backgroundColor: NAVY }}
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => {
                      setAddingSub(false);
                      setSubTitle('');
                    }}
                    className="text-sm text-gray-600 hover:underline disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={saving || editingSubIndex !== null}
                  onClick={() => setAddingSub(true)}
                  className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" /> Add sub-section
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function AddSectionPanel({
  sopId,
  frames,
  onDone,
}: {
  sopId: string;
  frames: Record<string, string>;
  onDone: () => void;
}) {
  const create = useCreateSopSection(sopId);
  const [heading, setHeading] = useState('');
  const invalid = !heading.trim();

  return (
    <div className="overflow-hidden rounded-lg border border-dashed border-gray-300 bg-white shadow-sm">
      <div className="flex items-center gap-2 px-3 py-2.5 text-white sm:px-4" style={{ backgroundColor: NAVY }}>
        <Plus className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
        <h2 className="min-w-0 flex-1 text-sm font-semibold tracking-wide">ADD SECTION</h2>
        <button
          type="button"
          onClick={onDone}
          disabled={create.isPending}
          className="rounded p-1.5 text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-50"
          aria-label="Cancel add section"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-3 border-t border-gray-100 px-4 py-4 sm:px-5">
        <div>
          <label htmlFor="add-sec-heading" className="block text-xs font-medium text-gray-600">
            Heading
          </label>
          <input
            id="add-sec-heading"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={heading}
            maxLength={255}
            placeholder="Section heading"
            onChange={(e) => setHeading(e.target.value)}
          />
        </div>
        <SopSectionEditor
          sopId={sopId}
          body=""
          frames={frames}
          saving={create.isPending}
          onSave={(markdown) => {
            if (invalid) return;
            create.mutate(
              { heading: heading.trim(), body: markdown },
              { onSuccess: onDone },
            );
          }}
          onCancel={() => {
            if (!create.isPending) onDone();
          }}
        />
      </div>
    </div>
  );
}

export default function SopDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useSop(id);
  const regenerate = useRegenerateSop(id ?? '');
  const cancel = useCancelSopGeneration(id ?? '');
  const remove = useDeleteSop();
  const updateStatus = useUpdateSop(id ?? '');
  const updateSection = useUpdateSopSection(id ?? '');
  const reorderSections = useReorderSopSections(id ?? '');
  const saveMarkup = useSaveSopMarkup(id ?? '');
  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addingSection, setAddingSection] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<'pdf' | 'doc' | null>(null);
  const [savingSectionId, setSavingSectionId] = useState<string | null>(null);
  const [markupMode, setMarkupMode] = useState(false);

  const citations = useMemo(() => new Map((data?.citations ?? []).map((c) => [c.marker, c])), [data?.citations]);
  const known = useMemo(() => new Set(citations.keys()), [citations]);
  const sections = data?.sections ?? [];
  const markupAnnotations = useMemo(
    () => parseSopMarkup(data?.sop?.markup_annotations),
    [data?.sop?.markup_annotations],
  );

  const persistMarkup = useCallback(
    (next: SopMarkupAnnotation[]) => {
      saveMarkup.mutate(next);
    },
    [saveMarkup],
  );

  const onMarkupAdd = useCallback(
    (item: SopMarkupAnnotation) => {
      persistMarkup([...markupAnnotations.filter((a) => a.id !== item.id), item]);
    },
    [markupAnnotations, persistMarkup],
  );

  const onMarkupRemove = useCallback(
    (annId: string) => {
      persistMarkup(markupAnnotations.filter((a) => a.id !== annId));
    },
    [markupAnnotations, persistMarkup],
  );

  if (isLoading) return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  if (error || !data) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Link to="/sop-synthesizer" className="text-sm hover:underline" style={{ color: NAVY }}>← Back to SOPs</Link>
        <p className="mt-4 text-sm text-red-600">SOP not found.</p>
      </div>
    );
  }

  const { sop, run, frames, video } = data;
  const generating = sop.status === 'generating';
  const editable = isSopEditable(sop.status);
  // Section body edit only when draft/changes_requested (not while generating).
  const sectionEditable = sop.status === 'draft' || sop.status === 'changes_requested';
  const markupEditable = canEditSopMarkup(sop.status) && !generating;
  const empty = sections.length === 0;
  const failed = !generating && run?.status === 'failed';
  const canGenerate = editable && !generating;

  return (
    <div className="min-h-full bg-[#F8F9FB]">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Link to="/sop-synthesizer" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{sop.title}</h1>
            {sop.description && <p className="mt-2 max-w-2xl whitespace-pre-line text-sm text-gray-600">{sop.description}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <span className="rounded-md bg-gray-200/80 px-2 py-1 font-medium text-gray-700">{shortSopId(sop.id)}</span>
              <span className="text-gray-400">Version</span>
              <span className="font-medium text-gray-700">v{sop.row_version}</span>
              <span className="text-gray-300">·</span>
              <span>Updated {formatWhen(sop.updated_at)}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {sectionEditable && !editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
              >
                <Pencil className="h-4 w-4" /> Edit
              </button>
            )}
            {!empty && !generating && (
              <button
                type="button"
                onClick={() => setMarkupMode((v) => !v)}
                aria-pressed={markupMode}
                title="Select text in a section to highlight or comment"
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                  markupMode
                    ? 'border-amber-400 bg-amber-50 text-amber-950'
                    : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                }`}
              >
                <Highlighter className="h-4 w-4" /> {markupMode ? 'Markup on' : 'Markup'}
              </button>
            )}
            <button
              type="button"
              disabled={empty || exportingFormat !== null}
              title={empty ? 'Generate the draft before downloading' : 'Download as PDF or Word'}
              className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
              style={{ backgroundColor: NAVY }}
              onClick={() => setShowExportModal(true)}
            >
              <Download className="h-4 w-4" /> Download
            </button>
            {canGenerate && !empty && (
              <button
                type="button"
                onClick={() => regenerate.mutate(undefined)}
                disabled={regenerate.isPending}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                title="Replaces the whole draft"
              >
                <RefreshCw className="h-4 w-4" /> Regenerate
              </button>
            )}
            {confirmDelete ? (
              <span className="inline-flex items-center gap-2 text-sm">
                <button
                  type="button"
                  className="rounded-md bg-red-600 px-3 py-2 text-white hover:bg-red-700 disabled:opacity-50"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(sop.id, { onSuccess: () => navigate('/sop-synthesizer') })}
                >
                  Confirm delete
                </button>
                <button type="button" className="text-gray-600 hover:underline" onClick={() => setConfirmDelete(false)}>Cancel</button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => editable && setConfirmDelete(true)}
                aria-disabled={!editable}
                title={editable ? 'Delete this SOP' : LOCKED_SOP_MESSAGE}
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${editable ? 'border-red-300 bg-white text-red-700 hover:bg-red-50' : 'cursor-not-allowed border-gray-200 text-gray-400'}`}
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            )}
          </div>
        </div>

        {!editable && <p className="mt-2 text-xs text-gray-500">{LOCKED_SOP_MESSAGE}</p>}

        <div className="mt-5 space-y-3">
          <StatusPills
            status={sop.status === 'generating' ? 'draft' : sop.status}
            disabled={sop.status === 'generating'}
            pending={updateStatus.isPending}
            onChange={(next) => updateStatus.mutate({ status: next })}
          />
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-xs font-semibold tracking-wide text-gray-500">Sources:</span>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 shadow-sm">
              <FileVideo className="h-3.5 w-3.5 text-gray-500" />
              {video.title ?? 'Walkthrough video'}
              {video.duration_seconds ? ` (${formatDuration(video.duration_seconds)})` : ''}
            </span>
          </div>
        </div>

        <div className="mt-6 space-y-5">
          {editing && sectionEditable && <EditDetailsForm sop={sop} onDone={() => setEditing(false)} />}

          {generating && <GenerationProgress run={run} onCancel={() => cancel.mutate()} cancelling={cancel.isPending} />}

          {failed && empty && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-800">The SOP could not be generated.</p>
              <p className="mt-1 text-sm text-red-700">{run?.error_message}</p>
            </div>
          )}

          {failed && !empty && (
            <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              The last regeneration failed ({run?.error_message}). The previous draft below is unchanged.
            </p>
          )}

          {canGenerate && empty && (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center shadow-sm">
              <h2 className="text-sm font-medium text-gray-900">{failed ? 'Try again' : 'No content yet'}</h2>
              <p className="mt-1 text-sm text-gray-500">
                The draft is written from the video in the background. It can take several minutes, and you can leave the page while it runs.
              </p>
              <label htmlFor="gen-guidance" className="sr-only">Guidance for the draft</label>
              <textarea
                id="gen-guidance"
                className="mx-auto mt-3 block w-full max-w-xl rounded-md border border-gray-300 px-3 py-2 text-left text-sm"
                rows={2}
                maxLength={2000}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Optional guidance, e.g. focus on the month-end steps"
              />
              <button
                type="button"
                onClick={() => regenerate.mutate(instructions.trim() || undefined)}
                disabled={regenerate.isPending}
                className="mt-3 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: NAVY }}
              >
                <Sparkles className="h-4 w-4" /> {failed ? 'Generate again' : 'Generate draft'}
              </button>
            </div>
          )}

          {markupMode && !empty && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
              Markup mode: select text in a section, then choose Highlight or Comment.
            </p>
          )}

          {!empty && (
            <div className="space-y-4">
              {sections.map((s, i) => (
                <SectionCard
                  key={s.id}
                  sopId={sop.id}
                  section={s}
                  index={i}
                  total={sections.length}
                  frames={frames}
                  knownMarkers={known}
                  onCite={setActiveMarker}
                  editable={sectionEditable}
                  saving={savingSectionId === s.id}
                  reordering={reorderSections.isPending}
                  markupMode={markupMode}
                  markupAnnotations={markupAnnotations}
                  markupEditable={markupEditable}
                  onMarkupAdd={onMarkupAdd}
                  onMarkupRemove={onMarkupRemove}
                  onMoveUp={() => {
                    if (i === 0) return;
                    const ids = sections.map((sec) => sec.id);
                    [ids[i - 1], ids[i]] = [ids[i], ids[i - 1]];
                    reorderSections.mutate(ids);
                  }}
                  onMoveDown={() => {
                    if (i >= sections.length - 1) return;
                    const ids = sections.map((sec) => sec.id);
                    [ids[i], ids[i + 1]] = [ids[i + 1], ids[i]];
                    reorderSections.mutate(ids);
                  }}
                  onBodySave={(sectionId, body, onDone, heading) => {
                    setSavingSectionId(sectionId);
                    updateSection.mutate(
                      { sectionId, body, heading },
                      {
                        onSuccess: onDone,
                        onSettled: () => setSavingSectionId((cur) => (cur === sectionId ? null : cur)),
                      },
                    );
                  }}
                />
              ))}
            </div>
          )}

          {sectionEditable && (
            addingSection ? (
              <AddSectionPanel
                sopId={sop.id}
                frames={frames}
                onDone={() => setAddingSection(false)}
              />
            ) : (
              <button
                type="button"
                onClick={() => setAddingSection(true)}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-8 text-sm font-medium hover:bg-gray-50"
                style={{ color: NAVY }}
              >
                <Plus className="h-5 w-5" /> Add Section
              </button>
            )
          )}

          {!empty && (
            <p className="text-xs text-gray-400">
              Drafted by AI from an automatic transcript. Numbered chips link each statement to the moment in the video it came from; review before relying on it.
            </p>
          )}
        </div>
      </div>

      <CitationPanel
        citation={activeMarker ? citations.get(activeMarker) ?? null : null}
        videoUrl={video.url}
        onClose={() => setActiveMarker(null)}
      />

      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md scale-100 transform rounded-lg bg-white p-8 transition-all duration-300">
            <div className="mb-6 flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100">
                  <Download className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Export SOP</h3>
                  <p className="text-sm text-gray-500">Choose a format to download</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !exportingFormat && setShowExportModal(false)}
                disabled={exportingFormat !== null}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                aria-label="Close export modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="mb-6 text-gray-700">
              Export <span className="font-semibold">{sop.title}</span> as a PDF document or a Word
              (.doc) file.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                disabled={exportingFormat !== null}
                className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-indigo-200 bg-indigo-50 px-4 py-6 text-indigo-700 transition-colors hover:border-indigo-300 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={async () => {
                  setExportingFormat('pdf');
                  try {
                    await runSopPdfDownload({
                      sop,
                      sections,
                      frames,
                      videoTitle: video.title,
                      videoDurationSeconds: video.duration_seconds,
                      markupAnnotations,
                    });
                    setShowExportModal(false);
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : 'Could not download PDF');
                  } finally {
                    setExportingFormat(null);
                  }
                }}
              >
                {exportingFormat === 'pdf' ? (
                  <Loader className="h-8 w-8 animate-spin" />
                ) : (
                  <FileText className="h-8 w-8" />
                )}
                <span className="font-semibold">PDF</span>
                <span className="text-xs text-indigo-600">Formatted report</span>
              </button>
              <button
                type="button"
                disabled={exportingFormat !== null}
                className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-green-200 bg-green-50 px-4 py-6 text-green-700 transition-colors hover:border-green-300 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={async () => {
                  setExportingFormat('doc');
                  try {
                    await downloadSopDoc({
                      sop,
                      sections,
                      frames,
                      videoTitle: video.title,
                      videoDurationSeconds: video.duration_seconds,
                      markupAnnotations,
                    });
                    setShowExportModal(false);
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : 'Could not download Word file');
                  } finally {
                    setExportingFormat(null);
                  }
                }}
              >
                {exportingFormat === 'doc' ? (
                  <Loader className="h-8 w-8 animate-spin" />
                ) : (
                  <FileText className="h-8 w-8" />
                )}
                <span className="font-semibold">Word</span>
                <span className="text-xs text-green-600">.doc document</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
